import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import MovementHistoryModal from "../components/MovementHistoryModal";
import { PlanNote } from "../components/PlanLockedHint";
import StockAdjustModal from "../components/StockAdjustModal";
import Toast from "../components/Toast";
import useProducts from "../hooks/useProducts";
import { useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranch } from "../services/branchService";
import { rememberedListUrl } from "../services/listMemory";
import { fetchBrands, fetchProductCategories } from "../services/catalogService";
import { hasFeature } from "../services/features";
import { fetchBranchProduct, updateBranchProduct } from "../services/productService";
import { labelFor } from "../services/enumLabels";

/**
 * What one branch sells, on its own terms: price, stock and the branch's own
 * online/active switches, plus stock adjustments and history. Products are
 * added here only from the catalogue (All Products → Branches); the product's
 * details are edited there too.
 *
 * `?adjust={productId}` opens the stock adjustment for that product — the
 * catalogue's Branches modal links here for a branch already selling it.
 */
export default function BranchProducts() {
  const { t, i18n } = useTranslation();
  const { id: branchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // The plan's inventory: without it products sell untracked — no stock
  // column, filters, adjustments or history (the API refuses them with 403).
  const tracksStock = hasFeature(getCurrentUser(), "inventory");

  const {
    products,
    meta,
    page,
    setPage,
    filters,
    applyFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    submitSearch,
    loading,
    error,
    reload,
  } = useProducts(branchId);

  // Back from a detail page lands where the user was.
  useScrollRestoration(!loading);

  const [branch, setBranch] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stockProduct, setStockProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [toast, setToast] = useState(location.state?.flash ?? null);

  // A stale id in the URL (deleted, or not this user's) falls back to "any".
  useValidOption(filters.category, categories.length ? categories : null, () =>
    applyFilters({ category: "" })
  );
  useValidOption(filters.brand, brands.length ? brands : null, () => applyFilters({ brand: "" }));

  // Consume the flash from the form page so a refresh does not repeat it.
  useEffect(() => {
    if (location.state?.flash) navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Deep link from the catalogue: open the adjustment, then drop the param so
  // a refresh or Back does not open it again.
  const adjustId = tracksStock ? new URLSearchParams(location.search).get("adjust") : null;
  useEffect(() => {
    if (!adjustId) return undefined;
    let cancelled = false;

    fetchBranchProduct(branchId, adjustId)
      .then((product) => {
        if (!cancelled) setStockProduct(product);
      })
      .catch((err) => {
        if (!cancelled) setToast({ type: "error", message: apiErrorMessage(err, t("products.loadFailed")) });
      });

    const params = new URLSearchParams(location.search);
    params.delete("adjust");
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustId, branchId]);

  // Header context and the filter dropdowns. A failure here costs only those.
  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchBranch(branchId), fetchProductCategories(), fetchBrands()])
      .then(([branchData, categoryList, brandList]) => {
        if (cancelled) return;
        setBranch(branchData);
        setCategories(categoryList);
        setBrands(brandList);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [branchId, i18n.language]);

  const currency = branch?.currency?.code ?? "";

  const money = (value) =>
    Number(value ?? 0).toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const filtersApplied =
    filters.search ||
    filters.category ||
    filters.brand ||
    filters.lowStock ||
    filters.isActive !== "" ||
    filters.isAvailableOnline !== "" ||
    filters.inStock !== "";

  /** Flip one of the branch's own switches; the price is sent back as it is. */
  const toggleSwitch = async (product, field) => {
    const offering = product.offering ?? {};
    setSwitching(product.id);
    try {
      await updateBranchProduct(branchId, product.id, {
        price: offering.price ?? product.price,
        isAvailableOnline: field === "online" ? !offering.is_available_online : offering.is_available_online,
        isActive: field === "active" ? !offering.is_active : offering.is_active,
      });
      reload();
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("products.switchFailed")) });
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("products.title")}</h1>
          <p className="text-sm text-ink-2">
            {branch ? t("products.subtitle", { branch: branch.name }) : t("products.subtitleFallback")}
          </p>
        </div>
        <div className="flex gap-2">
          {/* Always the Products list, as it was left — not history (a deep
              link or notification has none) and not the branch page. */}
          <Link to={rememberedListUrl("/products")} className="btn-secondary">
            {t("common.back")}
          </Link>
          <Link to="/products" className="btn-primary">
            {t("products.assignFromCatalogue")}
          </Link>
        </div>
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
        className="card mb-4 flex flex-wrap items-end gap-3 p-4"
      >
        <div className="min-w-[180px] flex-1">
          <label className="field-label" htmlFor="product-search">
            {t("products.filters.search")}
          </label>
          <input
            id="product-search"
            className="field-input"
            placeholder={t("products.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="filter-category">
            {t("products.filters.category")}
          </label>
          <select
            id="filter-category"
            className="field-input"
            value={filters.category}
            onChange={(e) => applyFilters({ category: e.target.value })}
          >
            <option value="">{t("products.filters.any")}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.parent ? `${category.parent.name} › ${category.name}` : category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="filter-brand">
            {t("products.filters.brand")}
          </label>
          <select
            id="filter-brand"
            className="field-input"
            value={filters.brand}
            onChange={(e) => applyFilters({ brand: e.target.value })}
          >
            <option value="">{t("products.filters.any")}</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        {tracksStock && (
          <div>
            <label className="field-label" htmlFor="filter-stock">
              {t("products.filters.stock")}
            </label>
            <select
              id="filter-stock"
              className="field-input"
              value={filters.inStock}
              onChange={(e) => applyFilters({ inStock: e.target.value })}
            >
              <option value="">{t("products.filters.any")}</option>
              <option value="1">{t("products.filters.inStock")}</option>
              <option value="0">{t("products.filters.outOfStock")}</option>
            </select>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="filter-active">
            {t("products.filters.status")}
          </label>
          <select
            id="filter-active"
            className="field-input"
            value={filters.isActive}
            onChange={(e) => applyFilters({ isActive: e.target.value })}
          >
            <option value="">{t("products.filters.any")}</option>
            <option value="1">{t("products.active")}</option>
            <option value="0">{t("products.inactive")}</option>
          </select>
        </div>

        {tracksStock && (
          <label className="flex items-center gap-2 pb-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line-strong"
              checked={filters.lowStock}
              onChange={(e) => applyFilters({ lowStock: e.target.checked })}
            />
            {t("products.filters.lowStockOnly")}
          </label>
        )}

        <div className="flex gap-2">
          <button type="submit" className="btn-primary">
            {t("products.filters.apply")}
          </button>
          {filtersApplied && (
            <button
              type="button"
              onClick={() => {
                resetFilters();
              }}
              className="btn-secondary"
            >
              {t("products.filters.clear")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {!tracksStock && <PlanNote className="mb-4">{t("plan.inventoryOff")}</PlanNote>}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : products.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-ink-2">
              {filtersApplied ? t("products.noMatches") : t("products.branchEmpty")}
            </p>
            {!filtersApplied && (
              <Link to="/products" className="btn-primary mt-3 inline-block">
                {t("products.assignFromCatalogue")}
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("products.table.product")}</th>
                  <th className="px-4 py-3">{t("products.table.price")}</th>
                  {tracksStock && <th className="px-4 py-3">{t("products.table.stock")}</th>}
                  <th className="px-4 py-3">{t("products.table.online")}</th>
                  <th className="px-4 py-3">{t("products.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {product.primary_image ? (
                          <img
                            src={product.primary_image.url}
                            alt=""
                            className="h-9 w-9 flex-shrink-0 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-raised text-xs text-ink-2">
                            —
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium">{product.name}</p>
                          <p className="truncate text-xs text-ink-2">
                            {product.category?.name ?? t("products.form.none")}
                            {product.brand?.name ? ` · ${product.brand.name}` : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-2" dir="ltr">
                      {money(product.price)}
                      {currency && <span className="ms-1 text-xs text-ink-2">{currency}</span>}
                      <span className="ms-1 text-xs text-ink-2">/ {labelFor("unit", product.unit)}</span>
                    </td>
                    {tracksStock && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium" dir="ltr">
                            {product.stock_quantity}
                          </span>
                          {product.is_out_of_stock ? (
                            <span className="rounded-full bg-danger-wash px-2 py-1 text-xs font-medium text-danger-text">
                              {t("products.outOfStock")}
                            </span>
                          ) : product.is_low_stock ? (
                            <span className="rounded-full bg-warning-wash px-2 py-1 text-xs font-medium text-warning-text">
                              {t("products.lowStock")}
                            </span>
                          ) : null}
                        </div>
                        {(product.offering?.stock_threshold ?? product.low_stock_threshold) != null && (
                          <p className="text-xs text-ink-2">
                            {t("products.thresholdAt", {
                              count: product.offering?.stock_threshold ?? product.low_stock_threshold,
                            })}
                            {product.offering?.low_stock_threshold == null && (
                              <span className="ms-1">{t("products.thresholdInherited")}</span>
                            )}
                          </p>
                        )}
                      </td>
                    )}
                    {/* The branch's own switches; clicking one flips it at this branch only. */}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        disabled={switching === product.id}
                        title={t("products.toggleHint")}
                        onClick={() => toggleSwitch(product, "online")}
                        className={
                          product.offering?.is_available_online
                            ? "rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text"
                            : "rounded-full bg-raised px-3 py-1 text-xs font-medium text-ink-2"
                        }
                      >
                        {product.offering?.is_available_online ? t("products.online") : t("products.offline")}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        disabled={switching === product.id}
                        title={t("products.toggleHint")}
                        onClick={() => toggleSwitch(product, "active")}
                        className={
                          product.offering?.is_active
                            ? "rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text"
                            : "rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text"
                        }
                      >
                        {product.offering?.is_active ? t("products.active") : t("products.inactive")}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex flex-wrap justify-end gap-3">
                        {tracksStock && (
                          <>
                            <button
                              type="button"
                              onClick={() => setStockProduct(product)}
                              className="text-sm font-medium text-accent-text hover:underline"
                            >
                              {t("products.adjustStock")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setHistoryProduct(product)}
                              className="text-sm font-medium text-ink-2 hover:underline"
                            >
                              {t("products.history.link")}
                            </button>
                          </>
                        )}
                        <Link
                          to={`/products/${product.id}/edit`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <StockAdjustModal
        branchId={branchId}
        product={stockProduct}
        open={Boolean(stockProduct)}
        onClose={() => setStockProduct(null)}
        onSaved={(saved) => {
          setToast({
            type: "success",
            message: t("products.stock.done", { name: saved.name, count: saved.stock_quantity }),
          });
          reload();
        }}
      />

      <MovementHistoryModal
        branchId={branchId}
        product={historyProduct}
        open={Boolean(historyProduct)}
        onClose={() => setHistoryProduct(null)}
      />


      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
